// Arcade — premium lobby with rich visual cards that depict each game.
// Game illustrations live in shared/GameIllustrations.jsx so the public landing
// page can reuse the EXACT same visuals.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Gamepad2, Trophy, ArrowRight, Sparkles, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { ILLUSTRATIONS, ORDER } from './games/shared/GameIllustrations';

export default function Arcade() {
    const [games, setGames] = useState([]);
    useEffect(() => { api.get('/games').then((r) => setGames(r.data)).catch(() => setGames([])); }, []);

    const sortedGames = [...games].sort((a, b) => {
        const ai = ORDER.indexOf(a.code); const bi = ORDER.indexOf(b.code);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    return (
        <div data-testid="arcade-page" className="relative">
            {/* Header */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-black via-slate-900 to-slate-950 text-white p-5 sm:p-7 mb-6 ring-1 ring-white/10">
                <div className="absolute inset-0 opacity-30 pointer-events-none">
                    <div className="absolute top-4 left-8 w-32 h-32 rounded-full bg-brand-yellow/40 blur-3xl" />
                    <div className="absolute bottom-2 right-12 w-40 h-40 rounded-full bg-brand-red/40 blur-3xl" />
                    <div className="absolute top-1/3 right-1/4 w-24 h-24 rounded-full bg-emerald-500/30 blur-3xl" />
                </div>
                <div className="relative z-10 flex items-center gap-3">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-[#00DB79] to-emerald-600 flex items-center justify-center shadow-lg"
                        style={{ boxShadow: '0 0 30px rgba(0,219,121,0.55), inset 0 2px 0 rgba(255,255,255,0.4)' }}>
                        <Gamepad2 className="h-6 w-6 sm:h-7 sm:w-7 text-brand-black" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] font-bold text-[#9DF5CE] inline-flex items-center gap-1">
                            <Sparkles className="h-3 w-3" /> Learning Arcade
                        </div>
                        <h1 className="font-display font-extrabold text-2xl sm:text-4xl text-white">Play to learn. Learn to win.</h1>
                        <p className="text-xs sm:text-sm text-slate-300 mt-1">{games.length} games · Daily challenges · Real points · Real badges</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {sortedGames.map((g, i) => (
                    <GameTile key={g.code} game={g} index={i} />
                ))}
            </div>

            <div className="mt-8 flex items-start gap-3 p-4 rounded-3xl border" style={{ background: 'rgba(0,219,121,0.08)', borderColor: 'rgba(0,219,121,0.3)' }}>
                <Trophy className="h-6 w-6 mt-0.5" style={{ color: '#00DB79' }} />
                <div>
                    <h2 className="font-display font-bold text-base text-white">Arcade Champion Badge</h2>
                    <p className="text-xs sm:text-sm text-white/70 mt-0.5">Make it to the Top-3 in any game leaderboard to unlock the prestigious <b className="text-[#9DF5CE]">Arcade Champion</b> badge.</p>
                </div>
            </div>
        </div>
    );
}

function GameTile({ game, index }) {
    const conf = ILLUSTRATIONS[game.code] || { Render: () => null, bg: 'from-slate-700 to-slate-900', tag: 'GAME', accent: 'text-slate-200' };
    const Illustration = conf.Render;
    const isFeatured = game.code === 'pulse_power_quiz' || game.code === 'kbc_learning' || game.code === 'ludo_learning' || game.code === 'snake_ladder';

    return (
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: index * 0.06, type: 'spring', stiffness: 180, damping: 18 }}
            whileHover={{ y: -6, scale: 1.02 }}
            className="group"
        >
            <Link
                to={`/learn/arcade/${game.code}`}
                className={`relative block rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-xl bg-gradient-to-br ${conf.bg} text-white transition-shadow hover:shadow-2xl`}
                data-testid={`arcade-tile-${game.code}`}
                style={{ minHeight: '280px' }}
            >
                {/* Illustration area */}
                <div className="relative h-44 sm:h-48 overflow-hidden">
                    <Illustration />
                    <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
                        style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 6px)' }} />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/40 backdrop-blur text-[9px] font-bold uppercase tracking-wider">
                        <span className={`${conf.accent}`}>● {conf.tag}</span>
                    </div>
                    {isFeatured && (
                        <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-brand-yellow to-amber-400 text-brand-black text-[9px] font-extrabold uppercase tracking-wider shadow"
                            style={{ boxShadow: '0 0 12px rgba(255,242,0,0.65)' }}>
                            <Star className="h-2.5 w-2.5" /> Featured
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="relative p-4 sm:p-5">
                    <div className="font-display font-extrabold text-lg sm:text-xl leading-tight">{game.name}</div>
                    <div className="text-[11px] sm:text-xs text-white/80 mt-1 line-clamp-2">{game.description}</div>
                    <div className="mt-3 flex items-center justify-between">
                        <div className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-bold bg-white/20 backdrop-blur px-2.5 py-1 rounded-full">
                            +{game.points_per_play} pts
                        </div>
                        <div className="inline-flex items-center gap-1.5 text-xs font-bold text-white group-hover:translate-x-1 transition-transform">
                            Play <ArrowRight className="h-3.5 w-3.5" />
                        </div>
                    </div>
                </div>

                {/* Hover glow */}
                <div className="absolute -inset-px pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                    style={{ boxShadow: '0 0 50px rgba(255,255,255,0.15), 0 20px 50px rgba(0,0,0,0.4)' }} />
            </Link>
        </motion.div>
    );
}
